import { useCallback, useEffect, useState } from 'react';
import { withTheme } from 'styled-components';
import { useAsync } from 'react-use';

import { RoutingEventEmitter, events as RoutingEvents } from 'modules/routing/context/events';

import { useRoutingMarkers, useItinerariesForRoutingMap } from './services';
import { mapOptions } from './map-options';

import {
  convertStopsToMaker,
  getRouteForDirectionsFromStops,
  getDirections,
  getStopStringForMemoize,
  getRoutedMarkerProps,
  getNonRoutedMarkerProps,
  getBoundsWithStops,
  getDirectionsRendererOptions,
  filterAlreadyExistingInRouteStops,
} from './utils';

import {
  GoogleMap,
  Marker,
  InfoWindow,
  useGoogleMap,
  DirectionsRenderer,
  useJsApiLoader,
} from '@react-google-maps/api';

import RoutingMapActions from './RoutingMapActions';
import RoutingMapInfoWindow from './RoutingMapInfoWindow';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const europeCenter = { lat: 54.526, lng: 15.2551 };

const containerStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
};

const RoutingMap = ({ theme, stops, filters, mapStopFilter }) => {
  const map = useGoogleMap();

  const itineraries = useItinerariesForRoutingMap({ filters, mapStopFilter });
  const markers = useRoutingMarkers({ itineraries, mapStopFilter });

  const [selectedStop, setSelectedStop] = useState(null);

  const markerStops = convertStopsToMaker(stops);
  const directionsRequest = getRouteForDirectionsFromStops(markerStops);
  const memoizeStopsString = getStopStringForMemoize(markerStops);

  const colorSchema = theme?.base?.colorSchema || {};

  const directionsRendererOptions = getDirectionsRendererOptions(colorSchema.primary500);
  const selectedItineraryDirectionsRendererOptions = getDirectionsRendererOptions(
    colorSchema.error500,
    { showIcons: false }
  );

  const selectedItinerary =
    selectedStop && itineraries.find((itinerary) => selectedStop.itinerary_id === itinerary.id);

  const directionsForSelectedItineraryRequest = getRouteForDirectionsFromStops(
    selectedItinerary?.stops
  );
  const selectedItineraryMemoizedStopString = getStopStringForMemoize(selectedItinerary?.stops);

  const handleAddToRouting = useCallback((itineraryId) => {
    RoutingEventEmitter.emit(RoutingEvents.ADD_ITINERARY, {
      itineraryId,
      baseItineraryEndpoint: 'itineraries',
    });
    setSelectedStop(null);
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setSelectedStop(null);
  }, []);

  const { value: directions } = useAsync(async () => {
    return getDirections(directionsRequest);
  }, [memoizeStopsString]);

  const { value: directionsForSelectedItinerary } = useAsync(async () => {
    return getDirections(directionsForSelectedItineraryRequest);
  }, [selectedItineraryMemoizedStopString]);

  useEffect(() => {
    if (!markerStops) {
      return;
    }
    const bounds = getBoundsWithStops(markerStops);
    map.fitBounds(bounds);
    //eslint-disable-next-line
  }, [memoizeStopsString]);

  const filteredMarkers = filterAlreadyExistingInRouteStops(markers, [
    ...(markerStops || []),
    ...(selectedItinerary?.stops || []),
  ]);

  return (
    <>
      {markerStops?.map((stop, key) => (
        <Marker
          key={stop.id}
          position={{ lat: stop.latitude, lng: stop.longitude }}
          clickable={false}
          {...getRoutedMarkerProps(colorSchema, stop, key)}
        />
      ))}
      {filteredMarkers.map((marker) => (
        <Marker
          key={marker.id}
          position={{ lat: marker.latitude, lng: marker.longitude }}
          clickable
          onClick={() => setSelectedStop(marker)}
          {...getNonRoutedMarkerProps(colorSchema, marker)}
        />
      ))}
      {selectedItinerary?.stops?.length &&
        selectedItinerary?.stops.map((marker) => (
          <Marker
            key={marker.id}
            position={{ lat: marker.latitude, lng: marker.longitude }}
            clickable={false}
            {...getNonRoutedMarkerProps(
              {
                primary: colorSchema.error500,
                white: colorSchema.white,
              },
              marker
            )}
          />
        ))}
      {!!selectedStop && (
        <InfoWindow
          position={{ lat: selectedStop.latitude, lng: selectedStop.longitude }}
          onCloseClick={handleInfoWindowClose}
          options={{ pixelOffset: new window.google.maps.Size(0, -30) }}
        >
          <RoutingMapInfoWindow stops={selectedItinerary?.stops} onAddClick={handleAddToRouting} />
        </InfoWindow>
      )}
      {!!directions && (
        <DirectionsRenderer directions={directions} options={directionsRendererOptions} />
      )}

      {!!directionsForSelectedItinerary && !!selectedItinerary && (
        <DirectionsRenderer
          directions={directionsForSelectedItinerary}
          options={selectedItineraryDirectionsRendererOptions}
        />
      )}
    </>
  );
};

const RoutingMapLoader = (props) => {
  const { isLoaded } = useJsApiLoader({
    id: 'routing-map-google-maps',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  if (!isLoaded) {
    return null;
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={europeCenter}
      zoom={4}
      options={mapOptions}
      data-testid="RoutingMap"
    >
      <RoutingMap {...props} />
    </GoogleMap>
  );
};

export default withTheme(RoutingMapLoader);
export { RoutingMapActions };
